import PropTypes from 'prop-types';
import React from 'react';
import Spinner from 'react16-spinjs';
import ArrowButton from '../../../common/components/ArrowButton';
import PassageLayout from '../../../common/layouts/passageLayout';
import Page from '../../../common/page/Page';
import PromptModal from '../../../common/components/PromptModal';
import { TRC_ARROW_BUTTON_DIRECTIONS } from '../../../constants/book';
import TRCAudioContainer from '../../audio/TRCAudioContainer';
import { TRC, TRC_QUESTION_TYPES } from '../../../constants/measure';
import { TRC_CONFIRM_BOOK_COMPLETE, ARIA_LABELS } from '../../../constants/text';
import OneTwoAnswerQuestionView from '../../../common/splitPageQuestion/OneTwoAnswerQuestion/OneTwoAnswerQuestionView';
import FillBlankQuestionView from '../../../common/splitPageQuestion/FillBlankQuestion/FillBlankQuestionView';
import ScreenErrorContainer from '../../../common/error/ScreenErrorContainer';
import LoadingPage from '../../../common/components/LoadingPage';
import { SPINNER, Direction } from '../../../constants/components';
import VRHelpPopup from '../../../common/components/helpPopup/VRHelpPopup';
import ArrowBackButton from '../../../common/components/ArrowBackButton';
import GoButtonDark from '../../../common/components/GoButtonDark';
import ToolTip from '../../../common/components/ToolTip';
import { setArPageTitle } from '../../troUtils';

export default class TRCQuestionsView extends React.Component {
    constructor(props) {
        super(props);
        setArPageTitle(props.isExternalURLSourcePresent, 'Answer Questions');
        this.onRetry = this.onRetry.bind(this);
        this.getBottomNavigation = this.getBottomNavigation.bind(this);
        this.getBookNavigation = this.getBookNavigation.bind(this);
        this.getQuestionByIndex = this.getQuestionByIndex.bind(this);
        this.getQuestions = this.getQuestions.bind(this);
        this.getStyles = this.getStyles.bind(this);
        this.handleBookComplete = this.handleBookComplete.bind(this);
        this.onLogOut = this.onLogOut.bind(this);
        this.hideTip = this.hideTip.bind(this);
        this.isLastQuestion = this.isLastQuestion.bind(this);
        this.onNextQuestion = this.onNextQuestion.bind(this);
        this.onBackButtonPress = this.onBackButtonPress.bind(this);
        this.state = {
            isLoading: false,
            isTipShown: true,
        };
    }

    async componentDidMount() {
        const {
            isFirstBook,
            isDataFetched,
            isComprehensionComplete,
            isExternalURLSourcePresent,
            arLanguage,
            startSingleQuestionAndComprehensionTimers,
            fetchContent,
            narrateScreen,
            bookId,
            bookTitle,
            trackStartTroQuestions,
        } = this.props;

        startSingleQuestionAndComprehensionTimers();

        if (!isDataFetched) {
            fetchContent();
        } else if (isFirstBook && !isComprehensionComplete) {
            // restart timer after Owly stops talking, needed for the first book
            narrateScreen(isExternalURLSourcePresent, arLanguage).then(
                startSingleQuestionAndComprehensionTimers,
            );
        }

        const isBookDataAvailable = bookId && bookTitle;
        if (isBookDataAvailable) {
            trackStartTroQuestions();
        }
        document.addEventListener('click', this.hideTip);
        document.addEventListener('touchstart', this.hideTip);
    }

    componentDidUpdate(prevProps) {
        const {
            executeApiCallSucceededEffect,
            executeApiCallFailedEffect,
            didApiCallSucceed,
            didApiCallFail,
            prevApiRequest,
            bookId,
            bookTitle,
            currentResult,
            narrateScreen,
            isExternalURLSourcePresent,
            arLanguage,
        } = this.props;
        const isBookDataAvailable = bookId && bookTitle;
        if (
            this.props.isDataFetched &&
            !prevProps.isDataFetched &&
            this.props.isFirstBook &&
            !this.props.isComprehensionComplete
        ) {
            narrateScreen(isExternalURLSourcePresent, arLanguage);
        }
        if (this.props.isDataFetched && !prevProps.isDataFetched && isBookDataAvailable) {
            this.props.trackStartTroQuestions();
        }
        if (currentResult && didApiCallSucceed && !prevProps.didApiCallSucceed) {
            executeApiCallSucceededEffect(prevApiRequest, currentResult.id, bookId);
        }
        if (currentResult && didApiCallFail && !prevProps.didApiCallFail) {
            executeApiCallFailedEffect(prevApiRequest, currentResult.id, bookId);
        }
    }

    componentWillUnmount() {
        this.props.cleanup();
    }

    onRetry() {
        if (
            this.props.totalQuestions === this.props.questionIndex + 1 ||
            this.props.isComprehensionComplete
        ) {
            this.handleBookComplete();
        } else {
            this.props.onNext();
        }
    }

    onBackButtonPress() {
        const { onBack, isExternalURLSourcePresent } = this.props;
        onBack(isExternalURLSourcePresent);
    }

    handleBookComplete() {
        this.props.stopComprehensionTimeTracking();
        this.setState({ isLoading: true });

        return this.props
            .confirmBookComplete(this.props.isComprehensionComplete, this.props.bookId)
            .finally(() => this.setState({ isLoading: false }));
    }

    hideTip() {
        if (!this.props.isScreenBlocked) {
            this.setState({ isTipShown: false });
            document.removeEventListener('click', this.hideTip);
            document.removeEventListener('touchstart', this.hideTip);
        }
    }

    getBookPage() {
        if (this.props.pages.length > 0) {
            const leftPage = this.props.pages[this.props.pageNum];
            return (
                <div aria-hidden={this.props.isConfirmPopupActive} className="book-page-wrapper">
                    <Page
                        template={leftPage.template}
                        paragraphs={leftPage.paragraphs}
                        pictureAttributes={leftPage.picture}
                        pageNumber={this.props.pageNum + 1}
                        indented={this.props.indented}
                    />
                    {this.getBookNavigation()}
                    {!this.props.isScreenBlocked && this.state.isTipShown && (
                        <ToolTip>Use text to help answer questions.</ToolTip>
                    )}
                </div>
            );
        }
        return null;
    }

    getBottomNavigation() {
        const updatedIndex = 1 + this.props.questionIndex;
        return (
            <div className="comprehension-bottom">
                {(this.props.isBackButtonActive || this.props.questionIndex > 0) && (
                    <ArrowBackButton
                        ariaLabel={ARIA_LABELS.GO_TO_PREV_QUESTION}
                        onClick={this.onBackButtonPress}
                        isEnabled={!this.props.isScreenBlocked && !this.props.isConfirmPopupActive}
                    />
                )}
                <div className="comprehension-progress">
                    {updatedIndex} of {this.props.totalQuestions}
                </div>
            </div>
        );
    }

    getBookNavigation() {
        return (
            <div className="book-navigation">
                <ArrowButton
                    ariaLabel={ARIA_LABELS.GO_TO_PREV_BOOK_PAGE}
                    onClick={this.props.goPrevBookPage}
                    pointingDirection={TRC_ARROW_BUTTON_DIRECTIONS.LEFT}
                    isEnabled={
                        !this.props.isFirstTRCPage &&
                        !this.props.isScreenBlocked &&
                        !this.props.isConfirmPopupActive
                    }
                />
                <ArrowButton
                    ariaLabel={ARIA_LABELS.GO_TO_NEXT_BOOK_PAGE}
                    onClick={this.props.goNextBookPage}
                    pointingDirection={TRC_ARROW_BUTTON_DIRECTIONS.RIGHT}
                    isEnabled={
                        !this.props.isLastTRCPage &&
                        !this.props.isScreenBlocked &&
                        !this.props.isConfirmPopupActive
                    }
                />
            </div>
        );
    }

    getQuestionByIndex({ itemId, type, possibleResponses, stimulus } = null) {
        switch (type) {
            case TRC_QUESTION_TYPES.MULTI_CHOICE:
                return (
                    <OneTwoAnswerQuestionView
                        measure={TRC}
                        possibleResponses={possibleResponses}
                        stimulus={stimulus}
                        questionType={TRC_QUESTION_TYPES.MULTI_CHOICE}
                        selectedIndex={this.props.selectedIndex}
                        onAnswerClick={this.props.selectAnswer}
                        isEnabled={!this.props.isScreenBlocked && !this.props.isConfirmPopupActive}
                    />
                );
            case TRC_QUESTION_TYPES.MULTIPLE_CORRECT_ANSWERS:
                return (
                    <OneTwoAnswerQuestionView
                        measure={TRC}
                        possibleResponses={possibleResponses}
                        stimulus={stimulus}
                        questionType={TRC_QUESTION_TYPES.MULTIPLE_CORRECT_ANSWERS}
                        selectedMultiChoiceIndexes={this.props.TRCSelectedResponse}
                        onAnswerClick={this.props.selectAnswer}
                        maxPossibleResponses={this.props.comprehensionItem.maxPossibleResponses}
                        isEnabled={!this.props.isScreenBlocked && !this.props.isConfirmPopupActive}
                    />
                );
            case TRC_QUESTION_TYPES.CLOZE_DROPDOWN:
                return (
                    <FillBlankQuestionView
                        measure={TRC}
                        possibleResponses={possibleResponses}
                        stimulus={stimulus}
                        questionId={itemId}
                        selectedIndex={this.props.selectedIndex}
                        onAnswerClick={this.props.selectAnswer}
                        isEnabled={!this.props.isScreenBlocked && !this.props.isConfirmPopupActive}
                    />
                );
            default:
                return false;
        }
    }

    isLastQuestion() {
        const { questionIndex, totalQuestions } = this.props;
        return questionIndex + 1 === totalQuestions;
    }

    onNextQuestion() {
        const { onNext, resetPagesViewed, trackAnsweredTroQuestion } = this.props;

        onNext();
        trackAnsweredTroQuestion();
        resetPagesViewed();
    }

    getQuestions() {
        return (
            <div aria-hidden={this.props.isConfirmPopupActive} className="question-pane">
                {this.getQuestionByIndex(this.props.comprehensionItem)}
                {this.getBottomNavigation()}
                <div>
                    {this.props.isNextButtonActive &&
                        (!this.props.isScreenBlocked ? (
                            <GoButtonDark
                                ariaLabel={
                                    this.isLastQuestion()
                                        ? ARIA_LABELS.DONE
                                        : ARIA_LABELS.GO_TO_NEXT_QUESTION
                                }
                                text={this.isLastQuestion() ? 'Done' : 'Next'}
                                onClick={this.onNextQuestion}
                                isEnabled={
                                    !this.props.isScreenBlocked && !this.props.isConfirmPopupActive
                                }
                            />
                        ) : (
                            !this.isLastQuestion() && <Spinner config={SPINNER.DARK} />
                        ))}
                </div>
            </div>
        );
    }

    getStyles() {
        const pageClasses = ['trc-measure'];
        if (this.props.comprehensionItem.type === TRC_QUESTION_TYPES.CLASSIFICATION) {
            pageClasses.push('dragboxes');
        }
        if (this.props.comprehensionItem.responseMatrix === TRC_QUESTION_TYPES.CLASSIFICATION_1X3) {
            pageClasses.push('threeboxes');
        }

        return pageClasses.join(' ');
    }

    onLogOut() {
        this.props.hideHelpPopup();
        this.setState({ isLoading: true });
        this.props.logUserOut();
    }

    render() {
        return (
            <div>
                <PassageLayout
                    class={this.getStyles()}
                    isScreenBlocked={this.props.isScreenBlocked || this.state.isLoading}
                    isGreyedOut={this.props.isGreyedOut}
                >
                    <ScreenErrorContainer onRetry={this.onRetry} />
                    <TRCAudioContainer />
                    {this.props.isHelpPopupVisible && (
                        <VRHelpPopup
                            logUserOut={this.onLogOut}
                            resultsVRStatuses={this.props.resultsVRStatuses}
                            resultVRErrorReasons={this.props.resultVRErrorReasons}
                            isAudioSavingInProgress={this.props.isOfflineAudioSyncInProgress}
                            isVrResultProcessingTimeout={this.props.isVrResultProcessingTimeout}
                            isExternalURLSourcePresent={this.props.isExternalURLSourcePresent}
                            isBenchmarkModeEnabled={this.props.isBenchmarkModeEnabled}
                            bookId={this.props.bookId}
                            bookTitle={this.props.bookTitle}
                            trackTroVrPopup={this.props.trackTroVrPopup}
                        />
                    )}
                    {this.getBookPage()}
                    {this.props.isDataFetched && this.getQuestions()}
                    <p aria-hidden={this.props.isConfirmPopupActive} className="book-level hidden">
                        {this.props.bookLevel}
                    </p>
                    {this.props.isConfirmPopupActive && (
                        <PromptModal
                            isEnabled={!this.props.isScreenBlocked}
                            message={TRC_CONFIRM_BOOK_COMPLETE}
                            onConfirm={this.handleBookComplete}
                            onCancel={this.props.returnToBook}
                            direction={Direction.LTR}
                        />
                    )}
                </PassageLayout>
                {this.state.isLoading && <LoadingPage spinnerConfig={SPINNER.DARK} />}
            </div>
        );
    }
}

TRCQuestionsView.propTypes = {
    isDataFetched: PropTypes.bool,
    isScreenBlocked: PropTypes.bool,
    isGreyedOut: PropTypes.bool,
    isFirstTRCPage: PropTypes.bool,
    isLastTRCPage: PropTypes.bool,
    isFirstBook: PropTypes.bool,
    onNext: PropTypes.func,
    goNextBookPage: PropTypes.func,
    goPrevBookPage: PropTypes.func,
    fetchContent: PropTypes.func,
    pages: PropTypes.array,
    pageNum: PropTypes.number,
    isNextButtonActive: PropTypes.bool,
    isBackButtonActive: PropTypes.bool,
    selectedIndex: PropTypes.number,
    selectAnswer: PropTypes.func,
    comprehensionItem: PropTypes.object,
    TRCSelectedResponse: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
    questionIndex: PropTypes.number,
    totalQuestions: PropTypes.number,
    maxPossibleResponses: PropTypes.number,
    onBack: PropTypes.func,
    confirmBookComplete: PropTypes.func,
    isConfirmPopupActive: PropTypes.bool,
    returnToBook: PropTypes.func,
    bookLevel: PropTypes.string,
    bookId: PropTypes.string,
    bookTitle: PropTypes.string,
    cleanup: PropTypes.func,
    indented: PropTypes.bool,
    narrateScreen: PropTypes.func,
    isHelpPopupVisible: PropTypes.bool,
    logUserOut: PropTypes.func,
    resultsVRStatuses: PropTypes.array,
    hideHelpPopup: PropTypes.func,
    currentResult: PropTypes.object,
    isComprehensionComplete: PropTypes.bool,
    isVrResultProcessingTimeout: PropTypes.bool,
    isOfflineAudioSyncInProgress: PropTypes.bool,
    resultVRErrorReasons: PropTypes.array,
    stopComprehensionTimeTracking: PropTypes.func,
    startSingleQuestionAndComprehensionTimers: PropTypes.func,
    executeApiCallSucceededEffect: PropTypes.func,
    executeApiCallFailedEffect: PropTypes.func,
    didApiCallSucceed: PropTypes.bool,
    didApiCallFail: PropTypes.bool,
    prevApiRequest: PropTypes.object,
    isExternalURLSourcePresent: PropTypes.bool,
    arLanguage: PropTypes.string.isRequired,
    isBenchmarkModeEnabled: PropTypes.bool.isRequired,
    trackStartTroQuestions: PropTypes.func,
    trackAnsweredTroQuestion: PropTypes.func,
    resetPagesViewed: PropTypes.func,
    trackTroVrPopup: PropTypes.func,
};

TRCQuestionsView.defaultProps = {
    indented: false,
    currentResult: null,
    pageNum: undefined,
    bookLevel: undefined,
    bookId: undefined,
    bookTitle: undefined,
    selectedIndex: undefined,
    isGreyedOut: false,
    isFirstBook: false,
    isLastTRCPage: false,
    isDataFetched: false,
    isFirstTRCPage: false,
    isScreenBlocked: false,
    isNextButtonActive: false,
    isBackButtonActive: false,
    isHelpPopupVisible: false,
    isConfirmPopupActive: false,
    isComprehensionComplete: false,
    isVrResultProcessingTimeout: false,
    isOfflineAudioSyncInProgress: false,
    questionIndex: 0,
    totalQuestions: 0,
    maxPossibleResponses: 0,
    pages: [],
    comprehensionItem: {},
    resultsVRStatuses: [],
    TRCSelectedResponse: [],
    resultVRErrorReasons: [],
    onBack: () => {},
    onNext: () => {},
    cleanup: () => {},
    logUserOut: () => {},
    fetchContent: () => {},
    returnToBook: () => {},
    selectAnswer: () => {},
    goNextBookPage: () => {},
    goPrevBookPage: () => {},
    hideHelpPopup: () => {},
    narrateScreen: () => {},
    confirmBookComplete: () => {},
    stopComprehensionTimeTracking: () => {},
    startSingleQuestionAndComprehensionTimers: () => {},
    executeApiCallSucceededEffect: () => {},
    executeApiCallFailedEffect: () => {},
    didApiCallSucceed: false,
    didApiCallFail: false,
    prevApiRequest: {},
    isExternalURLSourcePresent: false,
    trackStartTroQuestions: () => {},
    trackAnsweredTroQuestion: () => {},
    resetPagesViewed: () => {},
    trackTroVrPopup: () => {},
};
